<script>
/**
 * @component AIConfig
 * @description Configuración de módulos de IA. Cada módulo se guarda individualmente.
 */
import api from "@/services/api";

const MODULES = [
  {
    key: "consultation_analysis",
    label: "Análisis de atención clínica",
    description: "Transcripción y sugerencias durante la consulta",
  },
  {
    key: "patient_summary",
    label: "Resumen previo a la atención",
    description: "Resumen clínico del paciente antes de la consulta",
  },
  {
    key: "audit_analysis",
    label: "Análisis de auditoría médica",
    description: "Análisis de casos de auditoría con sugerencia de dictamen",
  },
  {
    key: "lab_analysis",
    label: "Análisis de laboratorio",
    description:
      "Extracción automática de indicadores desde PDF de resultados de laboratorio",
  },
  {
    // UC-REG-171 iter 4 — IA de mapeo del importer de liquidaciones.
    // Se dispara solo cuando el operador clickea "Sugerir con IA" en el
    // preview de importación (Cuenta Médica → Submissions → Nueva →
    // tab Importar archivo).
    key: "liquidation-import",
    label: "Importación de liquidaciones",
    description:
      "IA que mapea columnas de archivos Excel/CSV de prestadores al modelo interno cuando las heurísticas no alcanzan.",
  },
  {
    // UC-REG-288 — Codificación SNOMED CT de órdenes de imagen (POC). Solo
    // tiene efecto si el tenant tiene SNOMED_CODING_ENABLED y la org está en
    // modo `snomed-ai` (parametrización PRESCRIPTION_IMAGES_MODE). Con
    // provider OpenAI la misma key sirve para los embeddings.
    key: "snomed_coding",
    label: "Codificación SNOMED CT (órdenes de imagen)",
    description:
      "IA que traduce el pedido en lenguaje natural ('eco de panza') al concepto SNOMED CT, eligiendo solo entre candidatos del catálogo.",
  },
];

const PROVIDER_DEFAULTS = {
  claude: "claude-sonnet-4-20250514",
  openai: "gpt-4o",
  custom: "",
};

export default {
  name: "AIConfig",
  data() {
    return {
      modules: MODULES.map((m) => ({
        key: m.key,
        label: m.label,
        description: m.description,
        provider: "openai",
        model: "gpt-4o",
        apiKey: "",
        endpoint: "",
        systemPrompt: "",
        hasApiKey: false,
        loading: false,
        saving: false,
        expanded: false,
        showKey: false,
      })),
    };
  },
  created() {
    this.modules.forEach((m) => this.loadModule(m));
  },
  methods: {
    /**
     * @description Carga la config de un módulo desde la API
     */
    async loadModule(mod) {
      try {
        mod.loading = true;
        const data = await api
          .get(`/ai/config/module/${mod.key}`)
          .then((r) => r.data);
        mod.provider = data.provider || "openai";
        mod.model = data.model || "gpt-4o";
        mod.endpoint = data.endpoint || "";
        mod.systemPrompt = data.systemPrompt || "";
        mod.hasApiKey = data.hasApiKey || false;
        mod.apiKey = "";
      } catch (e) {
        // silencioso — usa defaults
      } finally {
        mod.loading = false;
      }
    },

    /**
     * @description Guarda la config de un módulo específico
     */
    async saveModule(mod) {
      try {
        mod.saving = true;
        const payload = {
          provider: mod.provider,
          model: mod.model,
          endpoint: mod.endpoint || "",
          systemPrompt: mod.systemPrompt || "",
        };
        if (mod.apiKey && mod.apiKey.trim()) {
          payload.apiKey = mod.apiKey.trim();
        }
        await api.put(`/ai/config/module/${mod.key}`, payload);
        this.$message.success(`${mod.label} guardado`);
        mod.apiKey = "";
      } catch (e) {
        this.$message.error(`Error al guardar ${mod.label}`);
      } finally {
        mod.saving = false;
      }
    },

    /**
     * @description Al cambiar proveedor, actualiza el modelo por defecto
     */
    onProviderChange(mod, provider) {
      mod.model = PROVIDER_DEFAULTS[provider] || "";
      mod.endpoint = "";
    },
  },
};
</script>

<template lang="pug">
.ai-config
  .ai-config__module(
    v-for="mod in modules"
    :key="mod.key"
    :class="{ 'ai-config__module--open': mod.expanded }"
  )
    .ai-config__module-header(@click="mod.expanded = !mod.expanded")
      .ai-config__module-header-left
        .ai-config__module-info
          span.ai-config__module-label {{ mod.label }}
          span.ai-config__module-desc {{ mod.description }}
      i.ai-config__chevron.el-icon-arrow-down

    .ai-config__module-body(v-show="mod.expanded")
      .ai-config__loading(v-if="mod.loading")
        i.el-icon-loading
        span Cargando...

      template(v-else)
        .ai-config__grid
          .ai-config__field
            label.ai-config__label Proveedor
            el-select(
              v-model="mod.provider"
              @change="val => onProviderChange(mod, val)"
              style="width:100%"
            )
              el-option(label="Claude (Anthropic)" value="claude")
              el-option(label="OpenAI (GPT)" value="openai")
              el-option(label="Custom" value="custom")

          .ai-config__field
            label.ai-config__label Modelo
            el-input(v-model="mod.model" placeholder="ej: gpt-4o")

          .ai-config__field.ai-config__field--full
            label.ai-config__label API Key
            .ai-config__key-row
              el-input(
                v-model="mod.apiKey"
                :type="mod.showKey ? 'text' : 'password'"
                placeholder="Dejá vacío para mantener la guardada"
              )
              button.ai-config__eye(@click="mod.showKey = !mod.showKey" type="button")
                i(:class="mod.showKey ? 'el-icon-hide' : 'el-icon-view'")
            p.ai-config__key-hint(v-if="mod.hasApiKey && !mod.apiKey")
              i.el-icon-check
              span  API key configurada

          .ai-config__field.ai-config__field--full(v-if="mod.provider === 'custom'")
            label.ai-config__label Endpoint
            el-input(v-model="mod.endpoint" placeholder="https://tu-servidor.com/v1")

          .ai-config__field.ai-config__field--full
            label.ai-config__label
              | Prompt del sistema
              span.ai-config__optional  (opcional)
            el-input(
              v-model="mod.systemPrompt"
              type="textarea"
              :rows="4"
              placeholder="Dejá vacío para usar el prompt por defecto"
            )

        .ai-config__module-footer
          el-button(
            type="primary"
            size="small"
            :loading="mod.saving"
            @click="saveModule(mod)"
          ) Guardar
</template>

<style lang="scss" scoped>
.ai-config {
  display: flex;
  flex-direction: column;
  gap: 10px;

  &__module {
    border: 1px solid $border-color;
    border-radius: 10px;
    overflow: hidden;

    &--open .ai-config__chevron {
      transform: rotate(180deg);
    }
  }

  &__module-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 16px;
    background: #f8fafc;
    cursor: pointer;
    transition: background 0.15s;

    &:hover {
      background: #f0f4f8;
    }
  }

  &__module-label {
    display: block;
    font-size: 13.5px;
    font-weight: 600;
    color: $navy;
  }

  &__module-desc {
    display: block;
    font-size: 12px;
    color: $text-secondary-new;
    margin-top: 2px;
  }

  &__chevron {
    font-size: 14px;
    color: $text-secondary-new;
    transition: transform 0.2s;
    flex-shrink: 0;
  }

  &__module-body {
    padding: 16px;
    border-top: 1px solid $border-color;
  }

  &__loading {
    display: flex;
    align-items: center;
    gap: 8px;
    color: $text-secondary-new;
    font-size: 13px;
    padding: 8px 0;
  }

  &__grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
    margin-bottom: 16px;
  }

  &__field {
    display: flex;
    flex-direction: column;
    gap: 6px;

    &--full {
      grid-column: 1 / -1;
    }
  }

  &__label {
    font-size: 11px;
    font-weight: 700;
    color: $navy;
    text-transform: uppercase;
    letter-spacing: 0.3px;
  }

  &__optional {
    font-size: 11px;
    font-weight: 400;
    color: $text-secondary-new;
    text-transform: none;
    letter-spacing: 0;
  }

  &__key-row {
    display: flex;
    gap: 8px;
  }

  &__key-hint {
    font-size: 12px;
    color: #16a34a;
    display: flex;
    align-items: center;
    gap: 4px;
    margin: 0;
  }

  &__eye {
    background: none;
    border: 1px solid $border-color;
    border-radius: 8px;
    padding: 8px 10px;
    cursor: pointer;
    color: $text-secondary-new;
    flex-shrink: 0;
    transition: all 0.2s;

    &:hover {
      color: $navy;
      border-color: $navy;
    }
  }

  &__module-footer {
    display: flex;
    justify-content: flex-end;
    padding-top: 12px;
    border-top: 1px solid $border-color;
  }
}
</style>
